// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { X, Zap, Search, Camera, Bell, ShoppingCart, Users, Star } from 'lucide-react';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * Pro Welcome Modal
 * Shown to Premium (£1.99/mo) and Family Plan (£2.99/mo) users on first login per session.
 */
const ProWelcomeModal = ({ onClose, userName, isFamily = false }) => {
  const trapRef = useFocusTrap({ active: true, onEscape: onClose });

  const planName = isFamily ? 'Family Plan' : 'Premium';
  const price = isFamily ? '£2.99/mo' : '£1.99/mo';
  const planEmoji = isFamily ? '👨‍👩‍👧‍👦' : '⚡';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70 backdrop-blur-sm animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pro-modal-title"
    >
      <div
        ref={trapRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h2 id="pro-modal-title" className="text-3xl font-bold mb-1">
            Welcome to {planName}! {planEmoji}
          </h2>
          <p className="text-blue-100 font-medium">You've unlocked the full PricePAL Compare experience</p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Thank You */}
          <section className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl">
            <h3 className="text-lg font-bold text-blue-900 mb-2 flex items-center gap-2">
              <Star className="w-5 h-5" />
              Thank You for Subscribing
            </h3>
            <p className="text-blue-800">
              Your {price} subscription gives you <strong>unlimited access</strong> to every PricePAL Compare feature.
              {isFamily && ' Share the savings with your whole family!'}
            </p>
          </section>

          {/* What's Included */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              What's Included
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">Unlimited Scans</div>
                  <div className="text-gray-500 text-xs">Scan any barcode to compare prices instantly</div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Search className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">Full Price Comparison</div>
                  <div className="text-gray-500 text-xs">Compare across all major UK supermarkets</div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">Unlimited Price Alerts</div>
                  <div className="text-gray-500 text-xs">Get notified the moment prices drop</div>
                </div>
              </div>

              {isFamily ? (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-800 text-sm">Family Sharing</div>
                    <div className="text-gray-500 text-xs">Share lists and savings with up to 4 members</div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-800 text-sm">Smart Shopping Lists</div>
                    <div className="text-gray-500 text-xs">Optimised lists that find the cheapest basket</div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Quick Start */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Quick Start
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center text-sm">
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">1️⃣</div>
                <div className="font-bold text-gray-800">Search or Scan</div>
                <div className="text-gray-500">Find any product</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">2️⃣</div>
                <div className="font-bold text-gray-800">Compare Prices</div>
                <div className="text-gray-500">See every store's price</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">3️⃣</div>
                <div className="font-bold text-gray-800">Save Money</div>
                <div className="text-gray-500">Shop at the cheapest</div>
              </div>
            </div>
          </section>

          {/* Help */}
          <section className="bg-blue-50 rounded-xl p-4 text-center">
            <h3 className="text-md font-bold text-blue-900 mb-1">Need help?</h3>
            <p className="text-blue-700 text-sm">
              Go to <strong>More {'>'} Support {'>'} Help & Support</strong> or email{' '}
              <strong>support@pricepalcompare.com</strong>
            </p>
          </section>
        </div>

        {/* Footer Action */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-lg font-bold py-4 rounded-xl shadow-lg hover:from-blue-600 hover:to-indigo-700 hover:scale-[1.02] transition-all transform flex items-center justify-center gap-2"
          >
            <span>{planEmoji}</span>
            Start Saving!
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProWelcomeModal;
